import { ChevronIcon } from '@/icons';
import { COLORS, pickThemeFontSize, pickThemeFontStyles, SHADOWS } from '@/theme';
import { Select, SelectProps } from 'antd';
import styled, { css } from 'styled-components';

const getStylesBySize = (size: SelectProps['size']) => {
  switch (size) {
    case 'small':
      return css`
        min-height: 1.5rem;
      `;
    case 'large':
      return css`
        min-height: 2.5rem;
      `;
    case 'middle':
      return css`
        min-height: 2rem;
      `;
    default:
      return css`
        min-height: 3rem;
      `;
  }
};

export const StyledWrapper = styled.div`
  position: relative;
  display: flex;
`;

export const StyledPrefix = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  font-size: ${pickThemeFontSize('20')};
  color: ${COLORS.GREY};
  margin: 0;
  padding: 0 0.625rem 0 1rem;
  z-index: 1;
`;

const arrowStyles = css`
  .ant-select-arrow .anticon:not(.ant-select-suffix) {
    pointer-events: none;
  }

  &.ant-select-open .ant-select-arrow {
    transform: rotate(-180deg) translateY(50%);
  }

  .ant-select-arrow {
    transition: transform 0.24s;
    color: ${COLORS.GREY};
    width: 1rem;
    height: 1rem;
    margin: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  .ant-select-clear {
    font-size: ${pickThemeFontSize('16')};
    background: ${COLORS.GREY};
    width: 1rem;
    height: 1rem;
    margin-top: -0.5rem;
    inset-inline-end: 0.75rem;
    background-color: ${COLORS.WHITE};

    .anticon {
      font-size: ${pickThemeFontSize('16')};
    }
  }
`;

const selectStyles = css`
  ${StyledPrefix} + &.ant-select {
    &-single:not(&-customize-input) .ant-select-selector,
    &-multiple:not(&-customize-input) .ant-select-selector {
      padding-left: 2.875rem;
      padding-right: 1.5rem;
    }

    &-multiple .ant-select-selection-placeholder,
    &-single .ant-select-selector .ant-select-selection-search {
      left: 2.875rem;
    }
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  background: ${COLORS.WHITE};
  border-radius: 0.25rem;
  ${pickThemeFontStyles('16', '24')};
  border: 1px solid ${COLORS.GREY_VERY_LIGHT};
  transition: border 0.3s, box-shadow 0.3s;
  ${({ size }) => getStylesBySize(size)}

  & .ant-select-selector {
    width: 100%;
    border: none !important;
    background: ${COLORS.WHITE};
    border-radius: 0.25rem;
    ${pickThemeFontStyles('16', '24')};
    padding: 0.5rem 0.75rem;
  }

  &:not(.ant-select-disabled):focus,
  &:not(.ant-select-disabled):hover {
    border: 1px solid ${COLORS.ORANGE_PRIMARY} !important;
    box-shadow: ${SHADOWS.BOTTOM} !important;
    outline: none !important;
  }

  ${arrowStyles};
  ${selectStyles};

  &.ant-select-multiple {
    .ant-select-selection-overflow {
      gap: 0.25rem;
      flex-wrap: nowrap;
    }
    .ant-select-selection-overflow-item {
      max-width: 12rem !important;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
` as typeof Select;

export const StyledDownWrapper = styled.div`
  & .ant-select-item {
    ${pickThemeFontStyles('16', '24')};

    &.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
      background-color: ${COLORS.ORANGE_PRIMARY} !important;
      color: ${COLORS.WHITE} !important;
      margin-top: 0.15rem;
    }

    padding: 0 0.5rem;

    &-option {
      & .ant-select-item-option-content {
        padding: 0.5rem 0;
      }

      & .ant-select-item-option-state {
        display: none;
      }
    }
  }
`;

export const StyledDownIcon = styled(ChevronIcon)`
  font-size: ${pickThemeFontSize('24')};
  path {
    fill: ${COLORS.GREY};
  }
`;

export const StyledTag = styled.div<{
  $isHighlighted?: boolean;
}>`
  ${pickThemeFontStyles('14', '16', 'SEMI_BOLD')};
  text-align: start;
  border: 1px solid ${COLORS.GREY};
  background: ${COLORS.WHITE};
  padding: 0.25rem 0.75rem;
  max-width: 7rem;
  border-radius: 3rem;
  height: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  & button {
    display: flex;
  }

  & .anticon {
    font-size: ${pickThemeFontSize('16')};
    color: ${COLORS.GREY};
    cursor: pointer;
  }
`;
